// frontend/src/pages/ProductoForm.jsx
import React, { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  Card,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBackRounded, ImageOutlined, Inventory2Outlined, SaveRounded } from '@mui/icons-material';
import AsyncAutocomplete from '../components/AsyncAutocomplete';
import {
  createProducto,
  fetchProducto,
  updateProducto,
} from '../api/productos';
import { fetchMarcasPage, fetchMarcaOptionById } from '../api/marcas';
import {
  fetchCategoria,
  searchCategoriasPadre,
  searchSubcategorias,
} from '../api/categorias';
import { subirImagen } from '../api/imagenes';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useToast } from '../contexts/ToastContext';

export default function ProductoForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation(); // ✅

  const { start, stop } = useLoading();
  const { showToast } = useToast();
  const confirm = useConfirm();

  // ✅ viene desde Productos.jsx cuando diste click en editar
  const listState = location.state?.listState;

  // Estados principales
  const [marcaSel, setMarcaSel] = useState(null);
  const [padreSel, setPadreSel] = useState(null);
  const [subcatSel, setSubcatSel] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dirty, setDirty] = useState(false);

  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    precio_compra: 0,
    precio_venta: '',
    stock: 0,
    marca_id: '',
    categoria_id: '',
    imagen_url: '',
    presentacion: '',
    libraje: '',
    sku: '', // ✅ ya lo usas abajo, mejor declararlo aquí
  });

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  useEffect(() => { stop(); }, [stop]);

  // Cargar producto al editar
  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const res = await fetchProducto(id);
        if (!mountedRef.current) return;

        const data = res.data || {};
        setForm({
          nombre: data.nombre || '',
          descripcion: data.descripcion || '',
          precio_compra: Number(data.precio_compra ?? 0),
          precio_venta: data.precio_venta ?? '',
          stock: Number(data.stock ?? 0),
          marca_id: data.marca_id ? String(data.marca_id) : '',
          categoria_id: data.categoria_id ? String(data.categoria_id) : '',
          imagen_url: data.imagen_url || '',
          presentacion: data.presentacion || '',
          libraje: data.libraje ?? '',
          sku: data.sku || '',
        });

        if (data.imagen_url) setPreview(data.imagen_url);

        if (data.marca_id) {
          const mOpt = await fetchMarcaOptionById(data.marca_id);
          if (mountedRef.current) setMarcaSel(mOpt);
        }

        if (data.categoria_id) {
          const subRes = await fetchCategoria(data.categoria_id);
          const sub = subRes?.data;
          if (sub) {
            const subOpt = { id: sub.id, label: sub.nombre, ...sub };
            setSubcatSel(subOpt);

            if (sub.parent_id) {
              const padreRes = await fetchCategoria(sub.parent_id);
              const p = padreRes?.data;
              if (p) setPadreSel({ id: p.id, label: p.nombre, ...p });
            } else {
              setPadreSel(subOpt);
            }
          }
        }

        setDirty(false);
      } catch {
        showToast({ message: 'Error cargando producto', severity: 'error' });
      }
    })();
  }, [id, showToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setDirty(true);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setDirty(true);
  };

  useEffect(() => {
    return () => {
      if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const mapCategoriaPage = (raw) => {
    const data = raw?.data ?? raw;
    const items = Array.isArray(data?.items) ? data.items : data;
    return {
      items: items.map((c) => ({ id: c.id, label: c.nombre, ...c })),
      hasMore: false,
    };
  };

  const fetchPadresPage = async (params) => mapCategoriaPage(await searchCategoriasPadre(params));
  const fetchSubcatsPage = async (params) =>
    padreSel?.id ? mapCategoriaPage(await searchSubcategorias(padreSel.id, params)) : { items: [] };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!subcatSel?.id) {
      showToast({ message: 'Selecciona una subcategoría', severity: 'warning' });
      return;
    }

    if (id) {
      const ok = await confirm({
        title: 'Actualizar producto',
        content: '¿Estás seguro?',
        confirmText: 'Sí, actualizar',
        cancelText: 'Cancelar',
      });
      if (!ok) return;
    }

    start();
    try {
      let imagen_url = form.imagen_url || '';
      if (file) {
        const res = await subirImagen(file);
        imagen_url = res?.data?.url ?? res?.url ?? imagen_url;
      }

      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        precio_venta: parseFloat(form.precio_venta) || 0,
        libraje: parseFloat(form.libraje) || 0,
        marca_id: marcaSel?.id ? Number(marcaSel.id) : null,
        categoria_id: subcatSel?.id ? Number(subcatSel.id) : null,
        imagen_url,
        presentacion: form.presentacion || '',
        sku: form.sku?.trim() || null,
      };

      // ✅ tu lógica: si editas y SKU viene vacío, no lo toques
      if (id) {
        const s = (form.sku || '').trim();
        if (s) payload.sku = s;
        else delete payload.sku;
      }

      if (id) {
        await updateProducto(id, payload);
        showToast({ message: 'Producto actualizado', severity: 'success' });
      } else {
        await createProducto(payload);
        showToast({ message: 'Producto creado', severity: 'success' });
      }

      // ✅ aquí está el cambio clave: vuelves a productos restaurando la página/filtros
      nav('/productos', {
        replace: true,
        state: { restoreListState: listState },
      });
    } catch (error) {
      const mensaje =
        error?.response?.data?.message ||
        'Este producto ya está creado';

      showToast({
        message: mensaje,
        severity: 'error',
      });

      stop();
    }
  };

  const handleCancel = async () => {
    if (dirty) {
      const ok = await confirm({
        title: 'Descartar cambios',
        content: '¿Salir sin guardar?',
        confirmText: 'Salir',
        cancelText: 'Seguir editando',
      });
      if (!ok) return;
    }

    start();

    // ✅ aquí también: regresar restaurando la lista
    nav('/productos', {
      replace: true,
      state: { restoreListState: listState },
    });
  };

  // ===========================================================
  //                   RENDERIZADO VISUAL
  // ===========================================================
  return (
    <Box sx={{ minHeight: '100%', py: { xs: 1, md: 2.5 } }}>
      <Container maxWidth="md" sx={{ pb: { xs: 3, md: 4 } }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '11px',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <Inventory2Outlined fontSize="small" />
          </Box>
          <Typography variant="overline" color="primary.main" fontWeight={800}>
            Catálogo
          </Typography>
        </Stack>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, textAlign: 'center' }}>
          {id ? 'Editar producto' : 'Nuevo producto'}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: 'center' }}
        >
          Completa los datos del producto sin necesidad de desplazarte demasiado.
        </Typography>

        <form onSubmit={handleSubmit}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: '18px',
              p: { xs: 2, md: 3 },
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {/* COLUMNA IZQUIERDA */}
              <Stack spacing={2}>
                <TextField
                  label="Código de barras (SKU)"
                  name="sku"
                  value={form.sku || ''}
                  onChange={handleChange}
                  placeholder="Escanea o escribe el código"
                  helperText="Déjalo vacío para generar un SKU al guardar; después podrás imprimir su código de barras desde Productos."
                  size="small"
                  autoFocus
                />

                <TextField
                  label="Nombre"
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  required
                  size="small"
                />
                <TextField
                  label="Descripción"
                  name="descripcion"
                  value={form.descripcion}
                  onChange={handleChange}
                  multiline
                  minRows={2}
                  size="small"
                />
                <TextField
                  label="Presentación / Tamaño (ej: 30 ml, 60 ml)"
                  name="presentacion"
                  value={form.presentacion || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />

                <TextField
                  label="Libraje / Peso (lb)"
                  name="libraje"
                  type="number"
                  value={form.libraje}
                  onChange={handleChange}
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                />

                <TextField
                  label="Precio venta"
                  name="precio_venta"
                  type="number"
                  value={form.precio_venta}
                  onChange={handleChange}
                  required
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                />

                <AsyncAutocomplete
                  label="Marca"
                  value={marcaSel}
                  onChange={(opt) => {
                    setMarcaSel(opt || null);
                    setForm((f) => ({ ...f, marca_id: opt?.id ? String(opt.id) : '' }));
                  }}
                  fetchPage={fetchMarcasPage}
                  placeholder="Buscar marca…"
                />
              </Stack>

              {/* COLUMNA DERECHA */}
              <Stack spacing={2}>
                <AsyncAutocomplete
                  label="Categoría"
                  value={padreSel}
                  onChange={(opt) => {
                    setPadreSel(opt || null);
                    setSubcatSel(null);
                    setForm((f) => ({ ...f, categoria_id: '' }));
                  }}
                  fetchPage={fetchPadresPage}
                  placeholder="Buscar categoría…"
                />

                <AsyncAutocomplete
                  label="Subcategoría"
                  value={subcatSel}
                  onChange={(opt) => {
                    setSubcatSel(opt || null);
                    setForm((f) => ({ ...f, categoria_id: opt?.id ? String(opt.id) : '' }));
                  }}
                  fetchPage={fetchSubcatsPage}
                  placeholder={padreSel ? 'Buscar subcategoría…' : 'Selecciona una categoría primero'}
                  disabled={!padreSel}
                />

                {id && (
                  <>
                    <TextField
                      label="Precio compra (informativo)"
                      name="precio_compra"
                      type="number"
                      value={form.precio_compra}
                      size="small"
                      InputProps={{ readOnly: true }}
                      helperText="Se actualiza automáticamente al registrar compras"
                    />
                    <TextField
                      label="Stock (informativo)"
                      name="stock"
                      type="number"
                      value={form.stock}
                      size="small"
                      InputProps={{ readOnly: true }}
                      helperText="Se actualiza con compras/ventas"
                    />
                  </>
                )}

                {/* IMAGEN */}
                <Stack spacing={1}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <ImageOutlined color="action" fontSize="small" />
                    <Typography variant="subtitle2">Imagen del producto</Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: '14px',
                      border: (theme) => `1px dashed ${theme.palette.divider}`,
                      backgroundColor: 'action.hover',
                      width: { xs: '100%', sm: 'fit-content' },
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button component="label" variant="outlined" size="small">
                      Subir imagen
                      <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                    </Button>

                    {preview && (
                      <Box
                        component="img"
                        src={preview}
                        alt="preview"
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: '12px',
                          objectFit: 'cover',
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                      />
                    )}
                  </Box>
                </Stack>
              </Stack>
            </Box>

            {/* BOTONES */}
            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              justifyContent="flex-end"
              spacing={1.25}
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackRounded />}
                onClick={handleCancel}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                type="submit"
                startIcon={<SaveRounded />}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {id ? 'Actualizar' : 'Guardar'}
              </Button>
            </Stack>
          </Card>
        </form>
      </Container>
    </Box>
  );
}
